"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { reportsApi } from "@/lib/api/services/reports.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonArticle } from "@/components/ui/skeleton";
import { FileText, Download, TrendingUp, Shield, Users, Zap, ArrowRight, AlertCircle } from "lucide-react";
import Link from "next/link";

interface Report {
  id: string;
  title: string;
  description: string;
  downloads: number;
  pages: number;
  category: string;
}

const categoryIcons: Record<string, React.ReactNode> = {
  Workplace: <Users className="h-5 w-5" />,
  "HR Tech": <Zap className="h-5 w-5" />,
  Talent: <TrendingUp className="h-5 w-5" />,
  "D&I": <Shield className="h-5 w-5" />,
};

export default function ResearchPage() {
  const { data: reports, isLoading, error } = useQuery({
    queryKey: ["research-reports"],
    queryFn: async () => {
      const data = await reportsApi.list({ page: 1, perPage: 8, sort: "downloads", order: "desc" });
      return ((data as unknown as { data?: Report[] }).data ?? []) as Report[];
    },
  });

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-5 py-20 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive/50" />
        <h2 className="mt-4 font-heading text-xl font-semibold text-foreground">Failed to load research reports</h2>
        <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
      </div>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
      <div className="text-center mb-12">
        <div className="eyebrow flex items-center justify-center gap-2 mb-4">
          <FileText className="h-3.5 w-3.5 text-primary" /> Research
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-foreground">
          Research Reports
        </h1>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Download free research reports and whitepapers on HR trends, talent management, workplace culture, and more.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          [1, 2, 3, 4].map((i) => <SkeletonArticle key={i} />)
        ) : !reports || reports.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground/30" />
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">No reports available</h3>
            <p className="mt-2 text-sm text-muted-foreground">Check back later for new research reports.</p>
          </div>
        ) : (
          reports.map((report: Report) => (
            <Card key={report.id} className="group hover:border-neutral-300 transition-all duration-150">
              <CardContent className="p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                  {categoryIcons[report.category] ?? <FileText className="h-6 w-6" />}
                </div>
                <Badge variant="secondary" size="sm" className="mb-3">{report.category}</Badge>
                <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                  {report.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-4">{report.description}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                  <span className="flex items-center gap-1"><Download className="h-3 w-3" /> {report.downloads?.toLocaleString() ?? "0"}</span>
                  <span className="text-neutral-300">·</span>
                  <span>{report.pages ?? 0} pages</span>
                </div>
                <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-xs gap-1">
                  <Link href={`/reports`}>
                    <span className="inline-flex items-center gap-1">
                      <Download className="h-3 w-3" /> Download PDF
                    </span>
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <div className="mt-12 text-center">
        <Button variant="outline" asChild>
          <Link href="/contact">Submit a Report</Link>
        </Button>
      </div>
    </main>
  );
}
